import { Fragment, useState } from 'react';
import {
    Tooltip,
    Dialog,
    DialogContent,
    DialogActions,
    Toolbar,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    TextField,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DownloadButton from './download_button';
import './edi_viewer.css';
import FileSaverBase from 'filesaver.js-npm';
import isEqual from 'lodash.isequal';

const FileSaver: any = FileSaverBase;

const StyledTable = styled(Table)({
    root: {
        flex: '1'
    },
    "& td": {
        padding: '5px'
    },
    "& th": {
        padding: '5px'
    }
});

interface EdiViewerProps {
    open?: boolean;
    onClose?: (...args: any[]) => void;
    raw?: string;
    fileName?: string;
    parsed?: any[];
    docType?: string;
    docid?: any;
    onClick?: (...args: any[]) => void;
    onChange?: (...args: any[]) => void;
    path?: string;
    [key: string]: any;
}

interface EdiViewerState {
    disabled: boolean;
    fileType: string;
    imageUrl: string | null;
    loaded: boolean;
    raw?: string;
    selected?: any;
    selectedType?: string | null;
}

const format = (text?: string): string | undefined => {
    if (!text || text.length < 4) {
        return text;
    }
    var term = null;
    if (text.indexOf("ISA") === 0) {
        var sep = text[3];
        var search = text.substring(0, 200);
        var split = search.split(sep);
        term = split[16][1];
    }
    else if (text.indexOf("UNA") === 0) {
        term = text[8];
    }
    else if (text.indexOf("UNB") === 0) {
        term = "'";
    }

    if (term === "\n" || term === "\r") {
        return text;
    }
    return text.split("\r").join("").split("\n").join("").split(term).join(term + "\n");
};

const renderElementValue = (elem: any) => {
    var tooltip = elem.SegName + "\n";
    if(elem.CodeDescription){
        tooltip += elem.Value + ' = ' + elem.CodeDescription + '\n';
    }
    if(elem.Errors && elem.Errors.length > 0){
        elem.Errors.map(e => tooltip += 'Error: ' + e + "\n")
    }
    return (
        <Tooltip title={tooltip} slotProps={{
            popper: {style: {whiteSpace: 'pre'}}
        }}><span key={elem.SegName} className={`val ${elem.Errors && elem.Errors.length > 0 ? 'error' : null}`}>{elem.Value}</span></Tooltip>
    );
};

const EdiViewer = (props: EdiViewerProps) => {
    const [state, setState] = useState<EdiViewerState>({disabled: false, fileType: 'pdf', imageUrl: null, loaded: false});

    // Derive selection state from incoming props (replaces getDerivedStateFromProps).
    // Faithful to the original: `raw` is only advanced when a set is actually selected,
    // so the derivation keeps re-running until parsed data becomes available.
    if (props.raw !== state.raw) {
        if (props.parsed && props.parsed[0] && props.parsed[0].Groups && props.parsed[0].Groups[0].Sets && props.parsed[0].Groups[0].Sets[0]) {
            const firstSet = props.parsed[0].Groups[0].Sets[0];
            setState(prev => ({...prev, selected: firstSet, selectedType: 'set', raw: props.raw}));
        } else if (state.selected !== null || state.selectedType !== null) {
            setState(prev => ({...prev, selected: null, selectedType: null}));
        }
    }

    const download = () => {
        var blob = new Blob([props.raw as any], { type: "application/octet-stream" });
        FileSaver.saveAs(blob, props.fileName || 'document.edi');
    };

    var { selected } = state;
    var { onClick, docType, docid, onChange, open, onClose, path, fileName, raw, parsed, ...rest } = props;
    var progress = <div style={{height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}><CircularProgress size={80} variant='indeterminate'/></div>;
    return (
        <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth {...rest}>
                <Toolbar className='lbtoolbar'>{'View EDI'}</Toolbar>
                <DialogContent style={{display: 'flex', maxHeight: props.raw ? '1000px' : '100px', transition: 'max-height 0.2s ease-in'}}>
                    {props.raw && state.selected ? (props.parsed && props.parsed.length > 0 ? <Fragment>
                        <List style={{borderRight: '1px solid rgba(224, 224, 224, 1)', overflow: 'auto'}}>
                            {props.parsed.map(env => <Fragment key={env.ControlNumber}>
                                    <ListItem {...({selected: isEqual(state.selected, env), button: true, onClick: () => setState(prev => ({...prev, selected: env, selectedType: 'envelope'}))} as any)}><ListItemText>Envelope {env.ControlNumber}</ListItemText></ListItem>
                                    <List style={{paddingLeft: '1rem'}}>
                                    {env.Groups && env.Groups.map(group => <Fragment key={group.ControlNumber}>
                                        <ListItem {...({selected: isEqual(state.selected, group), button: true, onClick: () => setState(prev => ({...prev, selected: group, selectedType: 'group'}))} as any)}><ListItemText>Group {group.ControlNumber}</ListItemText></ListItem>
                                            <List style={{paddingLeft: '1rem'}}>
                                            {group.Sets && group.Sets.map(set => <Fragment key={set.Number}>
                                                <ListItem {...({selected: isEqual(state.selected, set), button: true, onClick: () => setState(prev => ({...prev, selected: set, selectedType: 'set'}))} as any)}><ListItemText>Set {set.Number}</ListItemText></ListItem>
                                                </Fragment>)}
                                            </List>
                                        </Fragment>)}
                                    </List>
                                </Fragment>)}
                        </List>
                        <div style={{flex: 1, display: 'flex', flexDirection: 'column', marginLeft: '1rem', overflow: 'auto', position: 'relative'}}>
                        {selected && state.selectedType === 'set' && <Fragment>{(selected.SegmentErrors || selected.ElementErrors) && <div className='errorHolder'>
                            {selected.SegmentErrors.map(err => <div key={err} className='error'>{err}</div>)}
                            {selected.ElementErrors.map(err => <div key={err} className='error'>{err}</div>)}
                            </div>}<div style={{overflow: 'auto'}}>
                            {selected.Segments.map((seg, i) => <div key={i} className="seg">
            {seg.Errors ?
            <Tooltip title={seg.Errors.map(e => `Error: ${e}`).join("\n")} slotProps={{
                popper: {style: {whiteSpace: 'pre'}}
            }}><div className={`hier ${seg.Errors ? 'error' : ''}`}>{seg.Hierarchy}</div></Tooltip>
                : <div className={`hier ${seg.Errors ? 'error' : ''}`}>{seg.Hierarchy}</div>}
            <div className="line">
                <Tooltip title={seg.Id}><span className="id">{seg.Id}</span></Tooltip>
                {seg.Elements.map(elem =>
                    <Fragment key={elem.SegName}>
                <span className="sep"><span className="eleSep"></span></span>
                {elem.Components && elem.Components.length > 0 && elem.Components.map((com, ix) =>
                    <Fragment key={ix}>
                        {!(!(ix)) && <span className="component sep"><span className="comSep"></span></span>}
                        {renderElementValue(com)}
                    </Fragment>)}
                {!(elem.Components && elem.Components.length) && renderElementValue(elem)}
                </Fragment>)}
            </div>
        </div> )}

                        </div></Fragment>}
                        {selected && state.selectedType === 'envelope' && <Fragment>
                            <div>
                                <TextField disabled label="Sender" value={selected.SenderQualifier + ":" + selected.SenderId}/>
                                <TextField disabled label="Receiver" value={selected.ReceiverQualifier + ":" + selected.ReceiverId}/>
                            </div>
                            <div>
                                <TextField disabled label="Date" value={selected.Date}/>
                                <TextField disabled label="Time" value={selected.Time}/>
                            </div>
                            <div>
                                <TextField disabled label="Version" value={selected.Version}/>
                                <TextField disabled label="Control Number" value={selected.ControlNumber}/>
                            </div>
                            <div>
                                <TextField disabled label="Element Separator" value={selected.ElementSeparator}/>
                                <TextField disabled label="Component Separator" value={selected.ComponentSeparator}/>
                                <TextField disabled label="Segment Terminator" value={selected.SegmentTerminator}/>
                            </div>
                            <Toolbar className='lbtoolbar' style={{marginBottom: 0}}>Groups</Toolbar>
                            <StyledTable>
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{width: '8rem'}}>Control Number</TableCell>
                                    <TableCell style={{width: '4rem'}}>Type</TableCell>
                                    <TableCell style={{width: '5rem'}}>Date</TableCell>
                                    <TableCell style={{width: '5rem'}}>Time</TableCell>
                                    <TableCell>Sender ID</TableCell>
                                    <TableCell>Receiver ID</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                            {selected.Groups && selected.Groups.map((group) => <TableRow key={group.ControlNumber}>
                                <TableCell>{group.ControlNumber}</TableCell>
                                <TableCell>{group.TypeCode}</TableCell>
                                <TableCell>{group.Date}</TableCell>
                                <TableCell>{group.Time}</TableCell>
                                <TableCell>{group.SenderId}</TableCell>
                                <TableCell>{group.ReceiverId}</TableCell>
                                </TableRow>)
                            }
                            </TableBody>
                            </StyledTable>
                        </Fragment>}
                        {state.selected && state.selectedType === 'group' && <Fragment>
                            <div>
                                <TextField disabled label="Sender" value={selected.SenderId}/>
                                <TextField disabled label="Receiver" value={selected.ReceiverId}/>
                            </div>
                            <div>
                                <TextField disabled label="Date" value={selected.Date}/>
                                <TextField disabled label="Time" value={selected.Time}/>
                            </div>
                            <div>
                                <TextField disabled label="Version" value={selected.Version}/>
                                <TextField disabled label="Control Number" value={selected.ControlNumber}/>
                                <TextField disabled label="Type" value={selected.TypeCode}/>
                            </div>
                            <Toolbar className='lbtoolbar' style={{marginBottom: 0}}>Sets</Toolbar>
                            <StyledTable>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Control Number</TableCell>
                                    <TableCell>Type</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                            {selected.Sets && selected.Sets.map((set) => <TableRow key={set.Number}>
                                <TableCell>{set.Number}</TableCell>
                                <TableCell>{set.TypeCode}</TableCell>
                                </TableRow>)
                            }
                            </TableBody>
                            </StyledTable>
                        </Fragment>}
                        </div>
                        </Fragment>
                    : <pre style={{fontSize: '14px'}}>{format(props.raw)}</pre>)
                        : progress }
                </DialogContent>
                {props.raw && <DialogActions>
                    <DownloadButton color='primary' onClick={download}>Download</DownloadButton>
                </DialogActions>}
            </Dialog>
    );
};

export default EdiViewer;
